import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    Grid,
    Typography,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
} from "@mui/material";
import axiosInstance from '../../../axiosInstance';
import Base from "../../Base/views/Base";
import IncomeChart from "./IncomeChart";
import orderIcon from '../../assets/order-icon.png';
import reservationIcon from '../../assets/reservation-icon.png';
import incomeIcon from '../../assets/income-icon.png';
import visitorIcon from '../../assets/visitor-icon.png';
import monthIcon from '../../assets/month-icon.png';
import defaultItemImage from '../../assets/dash_food.jpg';
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

function checkInAsLocalDate(checkIn) {
    if (!checkIn) return null;
    const d = new Date(checkIn);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const LandingPage = () => {
    const [hotelID] = useState(localStorage.getItem("billing_app_hotel"));
    const userType = localStorage.getItem("billing_app_type");
    const [stats, setStats] = useState({});
    const [reservations, setReservations] = useState([]);
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const n = new Date();
        return new Date(n.getFullYear(), n.getMonth(), 1);
    });
    const [selectedDay, setSelectedDay] = useState(undefined);

    useEffect(() => {
        fetchHotelStats();
        fetchReservation();
    }, []);

    const fetchHotelStats = async () => {
        try {
            const response = await axiosInstance.post('/api/dashboard-stats/', {
                hotel_id: hotelID,
                date: new Date().toISOString().split('T')[0]
            });
            setStats({
                total_income_month: response?.data?.total_income_month,
                total_income_today: response?.data?.total_income_today,
                total_orders_today: response?.data?.total_orders_today,
                total_visitors_today: response?.data?.total_visitors_today,
                total_reservations_today: response?.data?.total_reservations_today,
                sales: response?.data?.sales
            });
        } catch (err) {
            console.error("Error fetching hotel stats:", err);
        }
    };

    const fetchReservation = async () => {
        try {
            const params = hotelID ? { hotel: hotelID } : {};
            const response = await axiosInstance.get('/api/reservation/', { params });
            setReservations(Array.isArray(response?.data) ? response.data : []);
        } catch (err) {
            console.error("Error fetching reservations:", err);
        }
    };

    const reservationDates = useMemo(
        () => reservations.map((r) => checkInAsLocalDate(r.check_in)).filter(Boolean),
        [reservations]
    );

    const modifiers = useMemo(() => ({ marked: reservationDates }), [reservationDates]);

    const checkInsInVisibleMonth = useMemo(() => {
        const y = calendarMonth.getFullYear();
        const m = calendarMonth.getMonth();
        return reservations.filter((r) => {
            const d = checkInAsLocalDate(r.check_in);
            return d && d.getFullYear() === y && d.getMonth() === m;
        });
    }, [reservations, calendarMonth]);

    const checkInsOnSelectedDay = useMemo(() => {
        if (!selectedDay) return [];
        const y = selectedDay.getFullYear();
        const mo = selectedDay.getMonth();
        const day = selectedDay.getDate();
        return reservations.filter((r) => {
            const d = checkInAsLocalDate(r.check_in);
            return d && d.getFullYear() === y && d.getMonth() === mo && d.getDate() === day;
        });
    }, [reservations, selectedDay]);

    const handleMonthChange = (month) => {
        setCalendarMonth(new Date(month.getFullYear(), month.getMonth(), 1));
        setSelectedDay(undefined);
    };

    const roomLabel = (room) => {
        if (!room) return '—';
        if (room.name) return room.name;
        if (room.no != null) return `Room ${room.no}`;
        return '—';
    };


    const topItems = [
        {
            name: "Paneer Butter Masala",
            sold: 42,
            total_income: 6300,
            image: defaultItemImage,
        },
        {
            name: "Chicken Biryani",
            sold: 36,
            total_income: 7200,
            image: defaultItemImage,
        },
        {
            name: "Veg Fried Rice",
            sold: 28,
            total_income: 4200,
            image: defaultItemImage,
        },
        {
            name: "Gulab Jamun",
            sold: 18,
            total_income: 900,
            image: defaultItemImage,
        }
    ]



    return (
        <Base>
            <Box sx={{ flexGrow: 1, pt: 2 }}>
                <Grid container spacing={1.5}>
                    {[{
                        label: userType === "Hotel" ? "Total Reservations" : "Total Orders",
                        value: stats?.total_reservations_today || 0,
                        icon: userType === "Hotel" ? reservationIcon : orderIcon
                    }, {
                        label: "Total Income",
                        value: stats?.total_income_today || 0,
                        icon: incomeIcon
                    }, {
                        label: "Total Visitors",
                        value: stats?.total_visitors_today || 0,
                        icon: visitorIcon
                    }, {
                        label: `Total Income ${new Date().toLocaleString('default', { month: 'long' })}`,
                        value: stats?.total_income_month || 0,
                        icon: monthIcon
                    }].map((stat, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card 
                                sx={{ 
                                    p: 2, 
                                    display: "flex", 
                                    alignItems: "center", 
                                    minHeight: 100,
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        transform: "translateY(-3px)",
                                        boxShadow: '0 4px 12px rgba(139, 68, 255, 0.15)',
                                    },
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                }}
                            >
                                <Avatar
                                    src={stat.icon}
                                    sx={{ 
                                        width: 48, 
                                        height: 48, 
                                        mr: 1.5, 
                                        bgcolor: 'white',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }}
                                    variant="rounded"
                                />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, fontWeight: 500, fontSize: '0.75rem' }}>
                                        {stat.label}
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" sx={{ color: 'primary.main', fontSize: '1.25rem' }}>
                                        {typeof stat.value === 'number' && stat.value >= 1000 
                                            ? `₹${(stat.value / 1000).toFixed(1)}K` 
                                            : stat.value}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ flexGrow: 1, pt: 2 }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={12} md={8}>
                        <IncomeChart salesData={stats.sales} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        {
                            ["Hotel", "Hotel and Restaurant"]?.includes(userType) ?
                                <Card
                                    sx={(theme) => ({
                                        p: 2,
                                        height: '100%',
                                        pl: 3,
                                        '& .rdp-root': {
                                            '--rdp-accent-color': theme.palette.primary.main,
                                            '--rdp-today-color': theme.palette.primary.main,
                                        },
                                    })}
                                >
                                    <DayPicker
                                        mode="single"
                                        className="dashboard-booking-calendar"
                                        navLayout="around"
                                        month={calendarMonth}
                                        onMonthChange={handleMonthChange}
                                        selected={selectedDay}
                                        onSelect={setSelectedDay}
                                        modifiers={modifiers}
                                        modifiersClassNames={{
                                            marked: 'dot-highlight',
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                        {checkInsInVisibleMonth.length} check-in{checkInsInVisibleMonth.length === 1 ? '' : 's'} in{' '}
                                        {calendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                                    </Typography>
                                    {selectedDay ? (
                                        <>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                {selectedDay.toLocaleDateString('default', {
                                                    weekday: 'short',
                                                    month: 'short',
                                                    day: 'numeric',
                                                })}
                                            </Typography>
                                            {checkInsOnSelectedDay.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    No check-ins on this day.
                                                </Typography>
                                            ) : (
                                                <List dense disablePadding>
                                                    {checkInsOnSelectedDay.map((r) => (
                                                        <ListItem key={r.id} disableGutters sx={{ py: 0.25 }}>
                                                            <ListItemText
                                                                primary={r.guest?.name || 'Guest'}
                                                                secondary={roomLabel(r.room)}
                                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                                                secondaryTypographyProps={{ variant: 'caption' }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}
                                        </>
                                    ) : null}
                                </Card> :
                                <Card sx={{ p: 2, height: '100%' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                                        🔥 Top Selling Items Today
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    {topItems?.length > 0 ? (
                                        <List dense>
                                            {topItems.map((item, index) => (
                                                <ListItem 
                                                    key={index} 
                                                    alignItems="flex-start" 
                                                    sx={{ 
                                                        mb: 1,
                                                        p: 1,
                                                        borderRadius: 1.5,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(139, 68, 255, 0.04)',
                                                            transform: 'translateX(4px)',
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            variant="rounded"
                                                            src={item.image || defaultItemImage}
                                                            sx={{ 
                                                                width: 40, 
                                                                height: 40,
                                                                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                                                            }}
                                                        />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        sx={{ ml: 1 }}
                                                        primary={
                                                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
                                                                {item.name}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                Sold: {item.sold} | ₹{item.total_income}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                            No sales data available for today.
                                        </Typography>
                                    )}
                                </Card>
                        }
                    </Grid>
                </Grid>
            </Box>
        </Base>
    );
};

export default LandingPage;
